/** @file A generic, reusable component for displaying a searchable and infinitely scrollable list. */
import moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import { CircleAlert, LoaderCircle, Search } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { assert, errors, expr, func } from '@codegrade/utils';
import { Icon } from '@/components/Icon';
import { LoadMoreButton } from '@/components/LoadMoreButton';
import * as utils from '@/utils';
export * as types from './types';
/**
 * Creates a generic component for a searchable, infinitely scrollable list.
 *
 * @template T The type of items in the list.
 * @param name The noun for the items being listed (e.g., "users", "courses").
 * @param root1 The component options.
 * @param root1.debounceTime The debounce duration for the search input.
 * @returns A Vue component definition.
 */
export function create(name, { debounceTime = moment.duration(250, 'ms'), } = {}) {
    const emptyListText = `There are no ${name}`;
    const noItemsFoundText = `No ${name} found`;
    const multipleItemsNoun = name;
    const singleItemNoun = name.replace(/s$/, '');
    return tsx
        .componentFactoryOf()
        .create({
        name: `infinite-search-scrollable-${name.replaceAll(' ', '-')}`,
        props: {
            /** An object that provides methods for getting, loading, and searching for items. */
            searcher: p.ofType().required,
            /** The search query string, typically bound with `.sync` or `v-model`. */
            query: p.ofType().required,
        },
        /** @returns The initial reactive state of the component. */
        data() {
            return {
                searchedItems: Maybe.empty(),
                searching: false,
                searchError: Maybe.empty(),
                searchNextPageToken: Maybe.empty(),
                initialLoading: false,
                initialLoadingError: Maybe.empty(),
            };
        },
        /** Called when the component is created. */
        created() {
            // Load the initial set of items to display before any search is performed.
            this.loadInitialItems();
            /**
             * A debounced version of the search method from the searcher prop.
             *
             * @param opts The parameters of the original search method.
             * @returns The return type of the search method.
             */
            const { func: debouncedSearch, cancel: cancelSearch } = func.debounceWithResult(this.searcher.search.bind(this.searcher), debounceTime);
            // Emit the current list of items whenever it changes.
            this.$watch(() => this.listItems, items => tsx.emitOn(this, 'onInput', items), { immediate: true });
            // Set up a watcher to handle search logic when the query changes.
            utils.watchedLoad({
                instance: this,
                /** Resets the search state before a new search is initiated. */
                reset() {
                    this.searching = this.cleanedQuery !== '';
                    this.searchedItems = Maybe.empty();
                    this.searchError = Maybe.empty();
                    this.searchNextPageToken = Maybe.empty();
                    cancelSearch();
                },
                /** @returns The reactive values to watch for changes. */
                getValues() {
                    return {
                        query: this.cleanedQuery,
                    };
                },
                /**
                 * Executes the debounced search when the query is non-empty.
                 *
                 * @param values A record of the watched values.
                 * @returns An object representing the outcome of the search.
                 */
                async load({ query }) {
                    if (query !== '') {
                        const res = await debouncedSearch({
                            query,
                        });
                        switch (res.tag) {
                            case 'debounced':
                                return { tag: 'cancelled' };
                            case 'result':
                                return {
                                    tag: 'search',
                                    nextToken: res.value.nextToken,
                                    items: res.value.data,
                                };
                            default:
                                return assert.AssertionError.assertNever(res);
                        }
                    }
                    else {
                        return { tag: 'cancelled' };
                    }
                },
                /**
                 * Sets the error state if a search operation fails.
                 *
                 * @param error A retryable error object.
                 */
                setError(error) {
                    this.searching = false;
                    this.searchedItems = Maybe.empty();
                    this.searchError = Maybe.of(error);
                    this.searchNextPageToken = Maybe.empty();
                },
                /**
                 * Sets the result state after a search operation completes.
                 *
                 * @param result The result of the load operation.
                 */
                setResult(result) {
                    switch (result.tag) {
                        case 'cancelled':
                            // Do nothing, the call to search is debounced and cancelled.
                            return;
                        case 'search':
                            this.searching = false;
                            this.searchNextPageToken = Maybe.fromNullable(result.nextToken);
                            this.searchedItems = Maybe.of(result.items);
                            this.searchError = Maybe.empty();
                            break;
                        default:
                            assert.AssertionError.assertNever(result);
                    }
                },
            });
        },
        methods: {
            /** Loads the first page of initial items to display before any search is performed. */
            async loadInitialItems() {
                this.initialLoading = true;
                this.initialLoadingError = Maybe.empty();
                try {
                    await this.searcher.loadItems({ tag: 'first-page' });
                }
                catch (caught) {
                    this.initialLoadingError = Maybe.of(errors.coerceToError(caught));
                }
                this.initialLoading = false;
            },
        },
        computed: {
            /** @returns The search query, trimmed and guaranteed to be a string. */
            cleanedQuery() {
                return (this.query ?? '').trim();
            },
            /** @returns The underlying paginated data from the searcher's `getItems` method. */
            baseItems() {
                return this.searcher.getItems();
            },
            /**
             * Consolidates various data properties into a single, matchable state object.
             * This simplifies the logic in other computed properties and the render function.
             *
             * @returns The current unified UI state of the component.
             */
            uiState() {
                const initialLoadingError = this.initialLoadingError;
                if (initialLoadingError.isJust()) {
                    return {
                        tag: 'error',
                        error: {
                            error: initialLoadingError.value,
                            retry: this.loadInitialItems.bind(this),
                        },
                    };
                }
                const searchError = this.searchError;
                if (searchError.isJust()) {
                    return { tag: 'error', error: searchError.value };
                }
                const searching = this.searching;
                if (searching) {
                    return { tag: 'searching' };
                }
                const searchedItems = this.searchedItems;
                if (searchedItems.isJust()) {
                    return {
                        tag: 'result',
                        isListEmpty: searchedItems.value.length === 0,
                        hasNextPage: this.searchNextPageToken.isJust(),
                    };
                }
                const initialLoading = this.initialLoading;
                if (initialLoading) {
                    return { tag: 'loading' };
                }
                const baseItems = this.baseItems;
                return {
                    tag: 'ready',
                    hasInitialItems: baseItems.data.length > 0,
                    hasNextPage: this.baseItems.state !== 'loaded-all',
                };
            },
            /** @returns The current list of items to be rendered, based on the `uiState`. */
            listItems() {
                switch (this.uiState.tag) {
                    case 'ready':
                        return this.baseItems.data;
                    case 'result':
                        return this.searchedItems.orDefault([]);
                    case 'error':
                    case 'loading':
                    case 'searching':
                        return [];
                    default:
                        return assert.AssertionError.assertNever(this.uiState);
                }
            },
            /** @returns The CSS classes for the status message container. */
            statusClass() {
                const clsContainer = 'w-full min-h-16 py-2! flex items-center justify-center gap-x-1 gap-y-2';
                if (this.uiState.tag === 'error') {
                    const clsCritical = 'text-critical-60 dark:text-critical-20';
                    return [clsContainer, clsCritical];
                }
                const clsNeutral = 'text-neutral-60 dark:text-neutral-20';
                return [clsContainer, clsNeutral];
            },
            /** @returns A VNode that displays the current status of the list (e.g., loading, error, empty). */
            statusNode() {
                let statusClass = this.statusClass;
                const child = expr.match(this.uiState, 'tag', {
                    loading: () => utils.renderScopedSlot(undefined, this.$scopedSlots.statusLoader, () => [<Icon icon={LoaderCircle} spin/>, 'Loading']),
                    ready: (ready) => {
                        if (!ready.hasInitialItems) {
                            return utils.renderScopedSlot({ searching: false }, this.$scopedSlots.noItemsFound, () => emptyListText);
                        }
                        let moreAvailable = '';
                        if (ready.hasNextPage) {
                            moreAvailable = ', more available';
                        }
                        statusClass = ['sr-only', statusClass];
                        const count = this.listItems.length;
                        const resultNoun = count === 1 ? singleItemNoun : multipleItemsNoun;
                        return `${count} ${resultNoun} loaded${moreAvailable}`;
                    },
                    result: result => {
                        if (result.isListEmpty && !result.hasNextPage) {
                            return utils.renderScopedSlot({ searching: true }, this.$scopedSlots.noItemsFound, () => noItemsFoundText);
                        }
                        let moreAvailable = '';
                        if (result.hasNextPage) {
                            moreAvailable = ', more available';
                        }
                        statusClass = ['sr-only', statusClass];
                        const count = this.listItems.length;
                        const resultNoun = count === 1 ? singleItemNoun : multipleItemsNoun;
                        return `${count} ${resultNoun} found${moreAvailable}`;
                    },
                    searching: () => utils.renderScopedSlot(undefined, this.$scopedSlots.statusSearching, () => [<Icon icon={LoaderCircle} spin/>, 'Searching']),
                    error: error => utils.renderScopedSlot(error, this.$scopedSlots.statusError, () => (<div class="flex flex-col items-center">
                                    <span>Failed to search: {error.error.error.message}</span>
                                    <button class="btn btn-sm btn-danger" onClick={error.error.retry}>
                                        Retry
                                    </button>
                                </div>)),
                });
                if (child == null) {
                    return undefined;
                }
                return (<div role="status" class={statusClass}>
                            {child}
                        </div>);
            },
            /** @returns The icon VNode for the search box, which changes based on the UI state. */
            searchBoxIconNode() {
                const stateTag = this.uiState.tag;
                const isSearching = stateTag === 'searching';
                const isError = stateTag === 'error';
                const commonClass = 'transition duration-200 col-start-1 row-start-1';
                return (<div class="grid">
                            <Icon spin icon={LoaderCircle} class={['text-current', commonClass, { hidden: !isSearching }]}/>
                            <Icon icon={CircleAlert} class={[
                        commonClass,
                        'text-critical-60 dark:text-critical-20',
                        { hidden: !isError },
                    ]}/>
                            <Icon icon={Search} class={[
                        'text-current',
                        commonClass,
                        { hidden: isSearching || isError },
                    ]}/>
                        </div>);
            },
            /** @returns The props for the `LoadMoreButton` component, or `Maybe.empty()` if not needed. */
            loadMoreBtnProps() {
                switch (this.uiState.tag) {
                    case 'ready': {
                        const baseItems = this.baseItems;
                        if (baseItems.state === 'not-started' ||
                            baseItems.state === 'loaded-all') {
                            return Maybe.empty();
                        }
                        return Maybe.of({
                            state: baseItems,
                            loadMore: async () => {
                                await this.searcher.loadItems({ tag: 'next-page' });
                            },
                        });
                    }
                    case 'result': {
                        const nextToken = this.searchNextPageToken.extract();
                        const data = this.searchedItems.orDefault([]);
                        if (nextToken === undefined) {
                            return Maybe.of({
                                state: { state: 'loaded-all', data },
                                loadMore: async () => { },
                            });
                        }
                        return Maybe.of({
                            state: { state: 'loaded', data },
                            loadMore: async () => {
                                const res = await this.searcher.search({
                                    query: this.cleanedQuery,
                                    nextToken,
                                });
                                this.searchNextPageToken = Maybe.fromNullable(res.nextToken);
                                this.searchedItems = Maybe.of([
                                    ...this.searchedItems.orDefault([]),
                                    ...res.data,
                                ]);
                            },
                        });
                    }
                    case 'error':
                    case 'loading':
                    case 'searching':
                        return Maybe.empty();
                    default:
                        return assert.AssertionError.assertNever(this.uiState);
                }
            },
            /** @returns The rendered `LoadMoreButton` component, or an empty VNode. */
            loadMoreBtnNode() {
                return this.loadMoreBtnProps.mapOrDefault(({ state, loadMore }) => (<LoadMoreButton state={state} loadMore={loadMore} runOnVisibile/>), utils.emptyVNode());
            },
        },
        /**
         * Renders the component by passing data to the default scoped slot.
         *
         * @returns The VNode for the component.
         */
        render() {
            return (<div>
                        {this.$scopedSlots.body({
                    items: this.listItems,
                    state: this.uiState,
                    statusNode: this.statusNode,
                    loadMoreButton: this.loadMoreBtnNode,
                    searchBoxIconNode: this.searchBoxIconNode,
                })}
                    </div>);
        },
    });
}
